import { getCookie, removeCookie, setCookie } from "typescript-cookie";
import "../global";
import { container } from "tsyringe";
import { SiteSettings } from "../shared/SiteSettings";
import { ServiceTokens } from "../shared/ServiceTokens";
import { CookiesNames } from "../gtm/Cookies";
import { Logger } from "./Logger";

const cookiesDomain = (): string => container.resolve<SiteSettings>(ServiceTokens.SiteSettings).CookiesDomain;

/**
 * Create or update a cookie that will expire in 1 year.
 * @remarks
 * The created cookie will also specify the domain indicated by the site specific settings.
 * @see SiteSettings
 * */
export function setOneYearCookie(name: string, value: string, path?: string): void {
  const duration = 365; // Days
  if (!path) {
    path = "/";
  }
  setCookie(name, value, { expires: duration, domain: cookiesDomain(), path: path });
}

/**
 * Delete a cookie based on the given name and the cookies domain specified by the site specific settings.
 * @see SiteSettings
 */
export function deleteCookieForDomain(name: string, path?: string): void {
  if (!path) {
    path = "/";
  }
  removeCookie(name, {
    domain: cookiesDomain(),
    path,
  });
}

/**
 * Check and validate the browser can write and read cookies
 */
export function validateWritingCookie(): boolean {
  let canWriteCookie = true;
  const key = "isd-writing-test";
  const value = "test";

  try {
    setCookie(key, value, { expires: 365, domain: cookiesDomain(), path: "/" });
    const cookieValue = getCookie(key);
    if (cookieValue !== value) {
      canWriteCookie = false;
      Logger.warn(`Unable to write/read cookie for domain "${cookiesDomain()}"`);
    }
    deleteCookieForDomain(key);
  } catch (e) {
    canWriteCookie = false;
    Logger.warn(`Unable to write/read cookie. Error: ${e}.`);
  }

  return canWriteCookie;
}

/**
 * Confirm that the BlueConic third-party cookie has been successfully stored and can be read by the browser.
 */
export function validateBlueConicCookie(): void {
  const cookieValue = getCookie(CookiesNames.blueConic);
  if (!cookieValue) {
    Logger.log(`Unable to get BlueConic "${CookiesNames.blueConic}" cookie after profile initialization`);
  } else {
    Logger.verbose(`Can access BlueConic cookie after profile initialization`);
  }
}

/**
 * Confirm that the Lotame third-party cookie has been successfully stored and can be read by the browser.
 */
export function validateLotameCookie(): void {
  const cookieValue = getCookie(CookiesNames.lotame);
  if (!cookieValue) {
    Logger.log(`Unable to get Lotame "${CookiesNames.lotame}" cookie after profile initialization`);
  }
}
